import React from 'react';
import cc from 'classcat';
import { faCheckCircle, faThumbtack } from '@fortawesome/pro-solid-svg-icons';
import Icon from '../../atoms/icon';
import Tag from '../../atoms/tag';
import Button from '../../molecules/button';
import Card from '../card';
import { AppsMapping } from '../../../contexts/apps';
import { IPMTemplate } from '../../../libs/pm';

interface PMTemplateCardMetaProps {
  template: IPMTemplate;
  apps: AppsMapping;
}

export const PMTemplateCardMeta = ({
  template,
  apps
}: PMTemplateCardMetaProps): JSX.Element => {
  const hasTags = template.tags && template.tags.length > 0;
  const hasApp = template.app && apps;
  const isChatTemplate = template.isChatTemplate;
  if (!hasTags && !hasApp && !isChatTemplate) {
    return null;
  }
  return (
    <div className="mt-4">
      {template.isChatTemplate && (
        <Tag
          style={{
            marginLeft: 0,
            marginRight: '13px'
          }}
          text="Chat"
          intent="success"
        />
      )}
      {template.app && apps && (
        <Tag
          style={{
            marginLeft: 0,
            marginRight: '13px'
          }}
          text={apps[template.app].name}
          intent="success"
        />
      )}
      {template.tags.map((tag, i) => (
        <Tag
          style={{
            marginLeft: 0,
            marginRight: '13px'
          }}
          key={i}
          text={tag}
          intent="primary"
        />
      ))}
    </div>
  );
};

interface PMTemplateCardProps {
  template: IPMTemplate;
  isSelected?: boolean;
  isPinned?: boolean;
  isPinUpdating?: boolean;
  apps: AppsMapping;
  onClick?: (template: IPMTemplate) => void;
  onTogglePin?: (template: IPMTemplate) => void;
}

const PMTemplateCard = ({
  template,
  isSelected,
  isPinned,
  isPinUpdating,
  apps,
  onClick,
  onTogglePin
}: PMTemplateCardProps): JSX.Element => {
  const classes = cc([
    'mb-4',
    isSelected ? 'bg-light-green border-green' : undefined
  ]);
  const hasOnClick = onClick ? true : false;
  const templateCard = (
    <Card className={classes} interactive={hasOnClick}>
      <div className="relative">
        {onTogglePin && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onTogglePin(template);
            }}
            className="absolute top-0 right-0 bg-none cursor-pointer pa-1 lh-1 z-1"
            title={isPinned ? 'Unpin template' : 'Pin template'}
            aria-label={isPinned ? 'Unpin template' : 'Pin template'}
            aria-pressed={!!isPinned}
            disabled={isPinUpdating}
            style={{ border: 'none', opacity: isPinUpdating ? 0.5 : 1 }}
          >
            <Icon
              icon={faThumbtack}
              className={isPinned ? 'text-blue' : 'text-light-gray'}
            />
          </button>
        )}
        <div className={onTogglePin ? 'pr-7' : undefined}>
          {isSelected && (
            <div className="mb-4">
              <b>
                <Icon className="text-green mr-2" icon={faCheckCircle} />{' '}
                Selected
              </b>
            </div>
          )}
          {template.body.map((segment) => (
            <span key={segment} className="block">
              {segment}
            </span>
          ))}
          <PMTemplateCardMeta template={template} apps={apps} />
        </div>
      </div>
    </Card>
  );
  if (!hasOnClick) {
    return templateCard;
  }
  return (
    <Button
      bare
      variant="minimal"
      fill
      onClick={() => onClick(template)}
      className="pa-0-important btn-text-wide mb-4"
    >
      {templateCard}
    </Button>
  );
};

export default PMTemplateCard;
